import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";

/**
 * Distinguishes how a Mandarin visitor arrived at / triggered an event:
 * - "google_translate" — switched the site to 中文 via the Google Translate widget
 * - "zh_page"          — visiting a hand-translated /zh/* page directly
 * - "cta"              — clicked a Mandarin call-to-action / conversion element
 */
export type ZhEventSource = "google_translate" | "zh_page" | "cta";

interface ZhFunnelOptions {
  source?: ZhEventSource;
  pagePath?: string;
  languageCode?: string;
}

/** True when the visitor is in a Mandarin context: on a /zh page or with Google Translate set to zh. */
export function isMandarinContext(): boolean {
  if (typeof window === "undefined") return false;
  if (window.location.pathname.startsWith("/zh")) return true;
  return /googtrans=\/[^/]+\/zh/i.test(document.cookie || "");
}

function inferSource(pagePath: string): ZhEventSource {
  if (pagePath.startsWith("/zh")) return "zh_page";
  if (/googtrans=\/[^/]+\/zh/i.test(typeof document !== "undefined" ? document.cookie : "")) {
    return "google_translate";
  }
  return "cta";
}

/**
 * Record a Mandarin visitor funnel event. Fires a Plausible event (existing
 * `trackEvent` pattern) AND persists to `zh_funnel_events` for the dashboard.
 * Best-effort: never throws, never blocks the UI.
 */
export async function trackZhFunnel(eventName: string, opts: ZhFunnelOptions = {}): Promise<void> {
  if (typeof window === "undefined") return;
  const pagePath = opts.pagePath ?? window.location.pathname;
  const source = opts.source ?? inferSource(pagePath);
  const languageCode = opts.languageCode ?? "zh";

  // Plausible (no-ops gracefully if unavailable)
  trackEvent(eventName, { source, page: pagePath, language: languageCode });

  // Persist for the Language Insights dashboard. Prefer the edge function so the
  // server can enrich the row with a country-level code (no IP, no precise
  // location). Fall back to a direct insert (country stays null -> "—") so logging
  // is never broken if the function is unavailable.
  const payload = {
    event_name: eventName,
    source,
    page_path: pagePath,
    language_code: languageCode,
  };

  try {
    const { error } = await supabase.functions.invoke("log-zh-funnel", { body: payload });
    if (error) throw error;
  } catch (fnErr) {
    try {
      await supabase.from("zh_funnel_events").insert(payload);
    } catch (err) {
      console.warn("ZH funnel log error:", err, fnErr);
    }
  }
}

/** Only logs when the visitor is in a Mandarin context (used for sitewide conversion points). */
export function trackZhFunnelIfMandarin(eventName: string, opts: ZhFunnelOptions = {}): void {
  if (!isMandarinContext()) return;
  void trackZhFunnel(eventName, opts);
}

/** Canonical funnel event names, ordered by stage for drop-off calculations. */
export const ZH_FUNNEL_STAGES: { key: string; label: string; emptyHint: string }[] = [
  { key: "Mandarin Page View", label: "Mandarin page views", emptyHint: "No Mandarin page views yet." },
  { key: "ZH Discover Link Click", label: "ZH Discover link clicks", emptyHint: "No ZH Discover link clicks yet." },
  { key: "Product Link Click", label: "Product link clicks", emptyHint: "No Mandarin product link clicks yet." },
  { key: "Product View", label: "Product views", emptyHint: "No Mandarin product views yet." },
  { key: "Add to Cart", label: "Add to cart", emptyHint: "No Mandarin add-to-cart events yet." },
  { key: "Contact Form Start", label: "Contact form starts", emptyHint: "No Mandarin contact form starts yet." },
  { key: "Contact Form Submit", label: "Contact form submissions", emptyHint: "No Mandarin contact form submissions yet." },
  { key: "Newsletter Signup", label: "Newsletter signups", emptyHint: "No Mandarin newsletter signups yet." },
  { key: "Balm & Bond Inquiry Click", label: "Balm & Bond inquiry clicks", emptyHint: "No Mandarin Balm & Bond inquiry clicks yet." },
  { key: "Sunrise & Stillness Inquiry Click", label: "Sunrise & Stillness inquiry clicks", emptyHint: "No Mandarin retreat inquiry clicks yet." },
  { key: "Booking Attempt", label: "Booking attempts", emptyHint: "No Mandarin booking attempts yet." },
];
